/**
 * Tenovi Device API Service
 * Provides methods to interact with Tenovi device endpoints
 */

import ormaApi from './OrmaApi';

/**
 * Order a Tenovi device for a member
 * @param {string} memberId - The member ID
 * @param {string} device - The device enum (e.g., 'TENOVI_BG', 'TENOVI_BP')
 * @returns {Promise} API response
 */
export const orderTenoviDevice = async (memberId, device) => {
  const response = await ormaApi.post('/api/tenovi/order-device', {
    memberId,
    device,
  });
  return response.data;
};

/**
 * Connect a Tenovi device to a member
 * @param {string} memberId - The member ID
 * @param {string} device - The device enum
 * @param {string} hardwareUuid - The gateway ID (12 characters)
 * @returns {Promise} API response
 */
export const connectTenoviDevice = async (
  memberId,
  device,
  hardwareUuid,
) => {
  const response = await ormaApi.post('/api/tenovi/connect-device', {
    memberId,
    device,
    hardwareUuid,
  });
  return response.data;
};

/**
 * Disconnect/unlink a Tenovi device from a member
 * @param {string} hwiDeviceId - The Tenovi device ID
 * @param {string} disconnectCategory - The disconnect category enum
 * @param {string} disconnectDetails - Additional details about the disconnect
 * @returns {Promise} API response
 */
export const unlinkTenoviDevice = async (
  hwiDeviceId,
  disconnectCategory,
  disconnectDetails,
) => {
  const response = await ormaApi.post(
    '/api/tenovi/disconnect-device',
    {
      hwiDeviceId,
      disconnectCategory,
      disconnectDetails,
    },
  );
  return response.data;
};

/**
 * Delete a Tenovi device permanently
 * @param {string} hwiDeviceId - The Tenovi device ID
 * @param {string} disconnectCategory - The disconnect category enum
 * @param {string} disconnectDetails - Additional details about the deletion
 * @returns {Promise} API response
 */
export const deleteTenoviDevice = async (
  hwiDeviceId,
  disconnectCategory,
  disconnectDetails,
) => {
  const response = await ormaApi.post('/api/tenovi/delete-device', {
    hwiDeviceId,
    disconnectCategory,
    disconnectDetails,
  });
  return response.data;
};

/**
 * Order Tenovi supplies/refills for a member
 * @param {string} memberId - The member ID
 * @param {string} supply - The supply enum (e.g., 'TENOVI_STRIPS_LANCETS', 'TENOVI_XL_BP_CUFF')
 * @returns {Promise} API response
 */
export const orderTenoviRefills = async (memberId, supply) => {
  const response = await ormaApi.post('/api/tenovi/order-refill', {
    memberId,
    supply,
  });
  return response.data;
};

/**
 * Fetch Tenovi devices based on query parameters
 * @param {Object} query - Query parameters for filtering devices
 * @param {string} [query.memberId] - Filter by member ID
 * @param {string} [query.owner_id] - Filter by owner/group ID
 * @param {boolean} [query.isBulkOrder] - Filter for bulk orders
 * @param {null} [query.deleted] - Filter by deleted status (null for non-deleted)
 * @param {null} [query.unlinked] - Filter by unlinked status (null for linked devices)
 * @param {string} [query.deviceEnum] - Filter by device type enum
 * @param {string} [query.status] - Filter by device status
 * @param {boolean} [query.isDemo] - Filter for demo devices
 * @returns {Promise<Array>} Array of Tenovi devices
 */
export const fetchTenoviDevices = async (query = {}) => {
  const response = await ormaApi.post('/api/tenovi/devices', {
    query,
  });
  return response.data.devices;
};

/**
 * Build Tenovi devices for a member with standard filters
 * Fetches only non-deleted and linked devices for the specified member
 * @param {string} memberId - The member ID
 * @returns {Promise<Array>} Array of Tenovi devices
 */
export const buildTenoviDevices = async (memberId) => {
  return await fetchTenoviDevices({
    memberId: memberId,
    // only want devices that have not been deleted or unlinked.
    deleted: null,
    unlinked: null,
  });
};
